import { useCallback, useEffect, useState } from "react";
import {
  isOnline as readIsOnline,
  onApiReachabilityChange,
  pingApi,
  startWebApiReachabilityMonitor,
} from "src/utils/isOnline";

/**
 * Custom hook to determine API reachability for the desktop and web app.
 *
 * @returns A boolean indicating whether the API is currently reachable.
 */
export const useIsOnline = () => {
  const [isOnline, setIsOnline] = useState(readIsOnline);

  const syncOnlineState = useCallback(() => {
    setIsOnline(readIsOnline());
  }, []);

  useEffect(() => {
    if (window.api?.onApiReachabilityChange) {
      window.api.onApiReachabilityChange(syncOnlineState);
      syncOnlineState();
      return;
    }

    startWebApiReachabilityMonitor();
    const unsubscribe = onApiReachabilityChange(syncOnlineState);
    pingApi().then(() => syncOnlineState());

    const handleLinkEvent = () => {
      pingApi().then(() => syncOnlineState());
    };

    window.addEventListener("online", handleLinkEvent);
    window.addEventListener("offline", handleLinkEvent);

    return () => {
      unsubscribe();
      window.removeEventListener("online", handleLinkEvent);
      window.removeEventListener("offline", handleLinkEvent);
    };
  }, [syncOnlineState]);

  return isOnline;
};
